<template>
  <v-form @submit.prevent>
    <default-select
      v-if="user.cloudData.length > 1"
      v-model="selectedConnection"
      :items="user.cloudData"
      :label="$t('connection')"
      :placeholder="$t('allConnections')"
      :persistent-placeholder="true"
      item-text="name"
      item-value="code"
      clearable
    >
    </default-select>
    <div v-if="loadingHotels">
      <v-card-text class="mt-3 pa-0">
        <span>{{ $t("loadingHotels") }}</span>
        <v-progress-linear
          indeterminate
          color="primary"
          class="mb-0"
        ></v-progress-linear>
      </v-card-text>
    </div>
    <default-autocomplete
      v-else
      id="hotel-select"
      ref="hotelSearch"
      v-model="hotel"
      :items="selectableHotels"
      item-text="select_name"
      item-value="select_id"
      :label="$t('hotel')"
      :search-input.sync="searchInput"
      :menu-props="{
        maxHeight: $vuetify.breakpoint.xsOnly ? 150 : 300,
        contentClass: 'notranslate',
      }"
      autocomplete="off"
      @change="changeHotel(hotel)"
    >
      <template #append-outer>
        <text-popover :text="$t('hotelSelectorPopupoverText')"></text-popover>
      </template>
    </default-autocomplete>
  </v-form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { axiosGet } from "@/api/requests";
import handleErrorStatus from "@/mixins/handleErrorStatus";
import snackBarMessages from "@/mixins/snackBarMessages";
import DefaultAutocomplete from "@/components/formFields/DefaultAutocomplete";
import DefaultSelect from "@/components/formFields/DefaultSelect";
import TextPopover from "@/components/globals/TextPopover";

import { getHotelStoreObject } from "@/helpers/objectHelpers";

export default {
  name: "HotelSelector",
  selectableHotels: [],
  components: {
    DefaultSelect,
    DefaultAutocomplete,
    TextPopover,
  },
  mixins: [snackBarMessages, handleErrorStatus],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      hotel: null,
      loadingHotels: false,
      searchInput: "",
      selectedConnection: null,
    };
  },
  computed: {
    selectableHotels() {
      let selectableHotels = this.$options.selectableHotels;
      if (this.selectedConnection) {
        selectableHotels = selectableHotels.filter(
          (hotel) => hotel.connection === this.selectedConnection
        );
      }
      return selectableHotels;
    },
    ...mapState(["currentHotel", "user", "connection"]),
  },
  watch: {
    showDialog: {
      immediate: true,
      handler(val) {
        if (val) {
          setTimeout(() => {
            this.focusHotelSearch();
          }, 300);
        }
      },
    },
    selectedConnection() {
      this.focusHotelSearch();
    },
  },
  created() {
    this.getSelectableHotels();
  },
  methods: {
    getSelectableHotels() {
      this.loadingHotels = true;
      const url = process.env.VUE_APP_DATA_URL + "/api/clouds";
      axiosGet(url, this.user.authToken)
        .then((response) => {
          if (response.status && response.status !== 200) {
            this.handleErrorStatus(response.status);
          } else if (Array.isArray(response)) {
            this.setHotelSelectOptions(response);
          } else {
            this.snackbarError(this.$t("loadingHotelsFailed"));
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loadingHotels = false;
        });
    },
    setHotelSelectOptions(hotels) {
      const allHotels = hotels.map((hotel) => ({
        code: hotel.hotelCode,
        name: hotel.name,
        select_name: hotel.name + " [" + hotel.hotelCode + "]",
        select_id: hotel.hotelCode + "_" + hotel.cloud,
        connection: hotel.cloud,
      }));
      allHotels.sort((a, b) => a.name.localeCompare(b.name));
      this.$options.selectableHotels = allHotels;
    },
    changeHotel(selected) {
      if (selected) {
        if (selected.connection === this.connection.code) {
          this.getSelectedHotel(selected);
        } else {
          const newConnection = this.user.cloudData.find(
            (connection) => connection.code === selected.connection
          );
          if (newConnection) {
            this.setConnection(newConnection);
            this.$nextTick(() => {
              this.getSelectedHotel(selected);
            });
          }
        }
      }
    },
    getSelectedHotel(selected) {
      this.$root.$loader.open(this.$t("loadingHotel"));
      const url =
        process.env.VUE_APP_DATA_URL +
        "/hotel?" +
        "name=" +
        this.connection.code +
        "&hotelcode=" +
        selected.code;
      axiosGet(url, this.user.authToken)
        .then((response) => {
          this.handleGetSelectedHotelResponse(response);
        })
        .catch((error) => {
          this.snackbarError(this.$t("loadingHotelFailed"));
        })
        .finally(() => {
          this.$root.$loader.close();
        });
    },
    focusHotelSearch() {
      if (this.$refs.hotelSearch) {
        this.$refs.hotelSearch.$refs.autocompleteInput.focus();
        this.$nextTick(() => {
          this.$refs.hotelSearch.$refs.autocompleteInput.$refs.input.click();
        });
      }
    },
    handleGetSelectedHotelResponse(response) {
      if (response.status && response.status !== 200) {
        this.handleErrorStatus(response.status);
      } else if (response.success) {
        const hotelDetails = getHotelStoreObject(response.hotel);
        this.selectHotel(hotelDetails);
        if (this.$route.name !== "dashboard") {
          this.$router.push("dashboard");
        }
        this.$emit("close-dialog");
      } else {
        this.snackbarError(this.$t("loadingHotelFailed"));
      }
    },
    ...mapActions(["selectHotel", "setConnection"]),
  },
};
</script>
